import { Context, Data, Effect, Layer } from 'effect'

export class BrowserDownloadFetchError extends Data.TaggedError('BrowserDownloadFetchError')<{
  cause: unknown
}> {}

export type BrowserDownloadSource = string | Blob | Uint8Array | File

export type BrowserDownloadsService = {
  readonly fetchBlob: (url: string) => Effect.Effect<Blob, BrowserDownloadFetchError>
  readonly save: (source: BrowserDownloadSource, filename?: string) => Effect.Effect<void>
}

export class BrowserDownloads extends Context.Service<BrowserDownloads, BrowserDownloadsService>()(
  'BrowserDownloads'
) {}

type BrowserDownloadTarget = {
  url: string
  filename: string | undefined
}

function resolveBrowserDownloadTarget(source: BrowserDownloadSource, filename: string | undefined) {
  return Effect.sync((): BrowserDownloadTarget => {
    if (typeof source == 'string') {
      return { url: source, filename }
    }

    const blob = source instanceof Uint8Array ? new Blob([source as unknown as BlobPart]) : source
    const url = URL.createObjectURL(blob)
    return {
      url,
      filename: filename ?? (source instanceof File ? source.name : undefined)
    }
  })
}

/**
 * Activates one browser download while owning the temporary anchor. The anchor
 * API exposes no download-completion signal, so blob URLs remain valid for the
 * browser page lifetime rather than risking a truncated download. Foreign DOM
 * failures remain defects.
 */
function saveBrowserDownload(source: BrowserDownloadSource, filename: string | undefined) {
  return Effect.gen(function* () {
    const target = yield* resolveBrowserDownloadTarget(source, filename)
    yield* Effect.acquireUseRelease(
      Effect.sync(() => {
        const anchor = document.createElement('a')
        anchor.href = target.url
        // Keep cross-origin download navigation from cancelling requests in Firefox.
        anchor.target = '_blank'
        anchor.rel = 'noopener'
        if (target.filename) {
          anchor.download = target.filename
        }
        anchor.style.display = 'none'
        document.body.appendChild(anchor)
        return anchor
      }),
      (anchor) => Effect.sync(() => anchor.click()),
      (anchor) => Effect.sync(() => anchor.remove())
    )
  })
}

/** Owns remote resource reads and browser download activation. */
export const browserDownloadsLayer = Layer.succeed(BrowserDownloads, {
  fetchBlob: (url) =>
    Effect.tryPromise({
      // URLs originate from the authenticated folder API and are fetched by the browser.
      // nosec
      try: () => fetch(url),
      catch: (cause) => new BrowserDownloadFetchError({ cause })
    }).pipe(
      Effect.flatMap((response) => {
        if (!response.ok) {
          return Effect.fail(
            new BrowserDownloadFetchError({
              cause: new Error(
                `Failed to fetch resource: ${response.status} ${response.statusText}`
              )
            })
          )
        }
        return Effect.tryPromise({
          try: () => response.blob(),
          catch: (cause) => new BrowserDownloadFetchError({ cause })
        })
      })
    ),
  save: saveBrowserDownload
})
